// CaptureLayout — page chrome for the field-capture routes: sticky brand
// bar + optional breadcrumb on top, content slot below (buildspec
// 20-ui §3).
//
// The brand bar is always present (BrandHeader + user-menu). The
// breadcrumb is rendered when segments are provided. Both are wrapped
// in a single sticky header so they scroll off together rather than
// producing two independent sticky bands.
//
// Height budget (AGENTS.md invariant #10, issue #236 + #258):
//   brand bar  ≤ 44 px on mobile (h-11), ≤ 56 px on sm+ (sm:h-14)
//   breadcrumb ≤ 36 px on mobile (min-h-9 items + py-1 wrapper)
//   total      ≤ 92 px on mobile → two-thumb counters remain reachable
// The held-work banner (UnsyncedCaptureBanner) renders as a CONDITIONAL top row
// inside this sticky header (audit PR #732 / M1+M2). It is present ONLY while the
// device is holding unsynced work (an exceptional, self-clearing state, not the
// steady case), adds a single thin text-xs row (~28 px), and is the surface that
// tells the surveyor work is unsaved — so the brief budget yields to it when it
// shows. It owns NO safe-area inset (the header's pt-safe is the single owner).

import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { BrandHeader } from '../../ui/brand-header'
import {
  CaptureBreadcrumb,
  type CaptureBreadcrumbProps,
} from './capture-breadcrumb'
import { CaptureUserMenu } from './capture-user-menu'
import { UnsyncedCaptureBanner } from './unsynced-capture-banner'

export interface CaptureLayoutProps {
  // Breadcrumb segments to render below the brand bar. Omit on
  // screens that have no hierarchy above them (e.g. the Base grid).
  breadcrumb?: CaptureBreadcrumbProps
  children: ReactNode
}

export function CaptureLayout({ breadcrumb, children }: CaptureLayoutProps) {
  const hasBreadcrumb =
    breadcrumb !== undefined &&
    (breadcrumb.base !== undefined ||
      breadcrumb.site !== undefined ||
      breadcrumb.building !== undefined ||
      breadcrumb.room !== undefined)

  return (
    <div className="flex min-h-screen flex-col bg-[color:var(--color-cc-cream)] text-[color:var(--color-cc-charcoal)]">
      {/*
       * Single sticky wrapper keeps the brand bar and breadcrumb in
       * one stacking context so they scroll together. Citycare brand
       * navigation: Forest background with backdrop blur and a hairline
       * Lime bottom border (skill: nav guidance). A JS-driven scroll
       * listener is intentionally avoided to keep this component
       * side-effect free.
       */}
      <header className="sticky top-0 z-20 border-b border-[color:var(--color-cc-lime)]/20 bg-[color:var(--color-cc-forest)]/95 text-[color:var(--color-cc-cream)] backdrop-blur supports-[backdrop-filter]:bg-[color:var(--color-cc-forest)]/85 pt-safe">
        {/*
         * Held-work banner — top row of the sticky header so it stays pinned
         * while the surveyor scrolls (M1). Reads the shared count from
         * UnsyncedCaptureProvider context (NOT the probe hook), so the layout
         * remounting per screen does not rebuild the probe machine (#6). Self-
         * hides when nothing is held, so it adds no height in the steady case.
         */}
        <UnsyncedCaptureBanner />
        {/* Brand bar — 44 px on mobile (h-11), 56 px on sm+ (sm:h-14) */}
        <div className="flex h-11 sm:h-14 items-center gap-2 px-4">
          <Link
            to="/capture"
            className="inline-flex min-h-11 min-w-11 items-center rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]"
          >
            <BrandHeader variant="topbar" />
          </Link>
          <span className="flex-1" aria-hidden="true" />
          <CaptureUserMenu />
        </div>

        {/* Breadcrumb row — only when hierarchy segments are present */}
        {hasBreadcrumb ? (
          <div className="border-t border-[color:var(--color-cc-cream)]/10 px-4 py-1">
            <CaptureBreadcrumb {...breadcrumb} />
          </div>
        ) : null}
      </header>

      <main className="flex-1 p-4 sm:p-6">{children}</main>
    </div>
  )
}
